import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuthStore } from '../lib/auth-store';
import { useAcademicOps } from '../lib/landings';
import { EmptyState } from '../components/ui/EmptyState';
import {
  LandingPage,
  KPIRow,
  CardsRow,
  LandingSkeleton,
  LandingError,
  LandingGuard,
  KPICard,
  LandingCard,
  LandingCardLine,
} from '../components/portal-shell';
import { timeAgo } from '../lib/format';

export const Route = createFileRoute('/_authed/academic-ops')({
  component: AcademicOpsPage,
});

function AcademicOpsPage() {
  return (
    <LandingGuard landingPath="/academic-ops">
      <AcademicOpsContent />
    </LandingGuard>
  );
}

function AcademicOpsContent() {
  const user = useAuthStore((s) => s.user);
  const query = useAcademicOps({ enabled: !!user });

  if (query.isLoading || (!query.data && !query.isError)) {
    return (
      <LandingPage
        title="Academic Operations"
        meta="Today's queue · Nalim"
        intro="Weekly-sheet reviews, attendance exceptions, and how your batches are tracking."
      >
        <LandingSkeleton />
      </LandingPage>
    );
  }
  if (query.isError || !query.data) {
    return (
      <LandingPage title="Academic Operations">
        <LandingError error={query.error} onRetry={() => query.refetch()} />
      </LandingPage>
    );
  }

  const { data } = query.data;
  const reviewQueueItems = data.weeklySheetReviewQueue.items;
  const hasBatches = data.batchStatusCards.length > 0;

  return (
    <LandingPage
      title="Academic Operations"
      meta={user ? `${user.displayName} · Nalim` : 'Nalim'}
      intro="Weekly-sheet reviews, attendance exceptions, and how your batches are tracking."
    >
      <KPIRow>
        <KPICard
          label="Sheets to review"
          value={data.weeklySheetReviewQueue.count}
          footnote={
            data.weeklySheetReviewQueue.count === 0 ? 'Queue clear' : 'Awaiting your approval'
          }
          tone={data.weeklySheetReviewQueue.count === 0 ? 'success' : 'default'}
        />
        <KPICard
          label="Attendance exceptions"
          value={data.attendanceExceptionsToday}
          footnote="Today"
        />
        <KPICard
          label="Batches tracked"
          value={data.batchStatusCards.length}
          footnote={data.batchStatusCards.length === 0 ? 'No batches yet' : 'Under your watch'}
        />
        <KPICard
          label="Avg attendance"
          value={
            hasBatches
              ? `${Math.round(
                  data.batchStatusCards.reduce(
                    (sum, b) => sum + b.attendanceRatePercent,
                    0,
                  ) / data.batchStatusCards.length,
                )}%`
              : '—'
          }
          footnote="Across your batches"
        />
      </KPIRow>

      <LandingCard
        title="Weekly sheets to review"
        className="md:col-span-2"
        titleAction={
          <Link
            to="/academics/weekly-sheet"
            className="text-xs font-medium text-gold-700 dark:text-gold-300 underline"
          >
            View all
          </Link>
        }
      >
        {reviewQueueItems.length > 0 ? (
          reviewQueueItems.slice(0, 6).map((item, idx, arr) => (
            <LandingCardLine key={item.id} last={idx === arr.length - 1}>
              <span className="font-medium">{item.batchName}</span>
              {item.teacherName ? (
                <span className="text-[color:var(--color-text-muted)]"> · {item.teacherName}</span>
              ) : null}
              <span className="text-[color:var(--color-text-muted)]">
                {' '}
                · week of {item.weekStartDate}
              </span>
              {item.submittedAt ? (
                <span className="text-[color:var(--color-text-muted)]">
                  {' '}
                  · submitted {timeAgo(item.submittedAt)}
                </span>
              ) : null}
            </LandingCardLine>
          ))
        ) : (
          <EmptyState
            title="All sheets reviewed"
            description="Teachers will queue new sheets here when their week wraps up."
            className="py-6"
          />
        )}
      </LandingCard>

      <CardsRow>
        {hasBatches ? (
          data.batchStatusCards.slice(0, 4).map((batch) => (
            <LandingCard
              key={batch.batchId}
              title={batch.batchName}
              titleAction={
                batch.courseName ? (
                  <span className="text-[11px] text-[color:var(--color-text-muted)]">
                    {batch.courseName}
                  </span>
                ) : null
              }
            >
              <LandingCardLine>
                Attendance:{' '}
                <span className="tabular-nums font-medium">{batch.attendanceRatePercent}%</span>
              </LandingCardLine>
              <LandingCardLine last>
                Last weekly sheet:{' '}
                <span className="font-medium">
                  {batch.lastWeeklySheetStatus
                    ? batch.lastWeeklySheetStatus.replace(/_/g, ' ')
                    : 'none'}
                </span>
              </LandingCardLine>
            </LandingCard>
          ))
        ) : (
          <LandingCard title="Batches">
            <EmptyState
              title="No batches yet"
              description="Once batches are created and you're assigned, status cards will appear here."
              className="py-6"
            />
          </LandingCard>
        )}
      </CardsRow>
    </LandingPage>
  );
}
